import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from './ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Skeleton } from './ui/skeleton';
import { Progress } from './ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useIsMobile } from './ui/use-mobile';
import { 
  Calendar, 
  Users, 
  BookOpen, 
  Download,
  Clock,
  MapPin,
  Eye,
  Archive,
  Bot,
  Menu,
  Home,
  ChevronRight,
  Sparkles,
  ArrowRight,
  GraduationCap,
  LayoutDashboard,
  Sun,
  Moon,
  Sunrise,
  Sunset,
  Activity,
  FolderOpen,
  FileText,
  TrendingUp,
  PlayCircle,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { FacultyScheduleCalendar } from './faculty/FacultyScheduleCalendar';
import { DigitalArchive } from './DigitalArchive';
import { AIAssistant } from './AIAssistant';
//import { ConnectionStatusBanner } from './ConnectionStatusBanner';
import { User as UserType } from '../types';
import { supabase } from '../utils/supabase/client';
import { api } from '../utils/api';
import { getCurrentAcademicPeriod } from '../utils/academicYear';
import { ARCHIVE_TARGET_EVENT, readArchiveTargetFromUrl } from '../utils/notificationLinks';

interface FacultyDashboardProps {
  user: UserType;
}

export function FacultyDashboard({ user }: FacultyDashboardProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [archiveStats, setArchiveStats] = useState({
    total: 0,
    'faculty-workload': 0,
    'iso-forms': 0,
    'unit-manual': 0,
    other: 0
  });
  const [facultySchedules, setFacultySchedules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  // Compute current academic year and semester dynamically
  const { academicYear: currentAcademicYear, semester: currentSemester, semesterLabel: currentSemesterLabel } = getCurrentAcademicPeriod();

  // Schedule filter state — default to current academic period
  const [filterAcademicYear, setFilterAcademicYear] = useState(currentAcademicYear);
  const [filterSemester, setFilterSemester] = useState<string>(String(currentSemester));

  const menuItems = [
    { value: 'overview', label: 'Overview', icon: Home },
    { value: 'schedule', label: 'My Schedule', icon: Calendar },
    { value: 'archive', label: 'Document Archive', icon: Archive }
  ];

  const handleMenuItemClick = (value: string) => {
    setActiveTab(value);
    setIsMenuOpen(false);
  };

  const getCurrentPageTitle = () => {
    const item = menuItems.find(item => item.value === activeTab);
    return item ? item.label : 'Overview';
  };

  // Filter schedules by selected academic year and semester
  const filteredSchedules = facultySchedules.filter(schedule => {
    const matchesYear = schedule.schoolYear === filterAcademicYear;
    const matchesSemester = filterSemester === 'all' || String(schedule.semester) === filterSemester;
    return matchesYear && matchesSemester;
  });

  // Derive available academic years from schedule data
  const availableAcademicYears = Array.from(
    new Set(facultySchedules.map(s => s.schoolYear).filter(Boolean))
  ).sort().reverse();
  // Ensure current academic year is always in the list
  if (!availableAcademicYears.includes(currentAcademicYear)) {
    availableAcademicYears.unshift(currentAcademicYear);
  }

  const semesterOptions = [
    { value: '1', label: '1st Semester' },
    { value: '2', label: '2nd Semester' },
    { value: '3', label: 'Summer' },
    { value: 'all', label: 'All Semesters' },
  ];

  const currentFilterSemesterLabel = semesterOptions.find(o => o.value === filterSemester)?.label || 'All Semesters';

  // Compute faculty subjects and stats from filtered schedules
  const facultySubjects: any[] = filteredSchedules.map(schedule => ({
    ...schedule.subject,
    id: schedule.id,
    schedule: {
      dayOfWeek: schedule.dayOfWeek,
      startTime: schedule.startTime,
      endTime: schedule.endTime
    },
    room: schedule.room,
    enrolledStudents: [] // TODO: Fetch enrolled students per subject
  }));
  
  const totalStudents = facultySubjects.reduce((sum, subject) => sum + (subject.enrolledStudents?.length || 0), 0);
  const totalClasses = filteredSchedules.length;

  const academicYear = filterAcademicYear;

  const stats = [
    { 
      title: 'Total Classes', 
      value: totalClasses, 
      icon: BookOpen, 
      color: 'bg-blue-500',
      gradient: 'from-blue-400 to-blue-600'
    },
    { 
      title: 'Subjects Teaching', 
      value: facultySubjects.length, 
      icon: BookOpen, 
      color: 'bg-purple-500',
      gradient: 'from-purple-400 to-purple-600'
    },
    { 
      title: 'Current Semester', 
      value: currentFilterSemesterLabel,
      icon: Calendar, 
      color: 'bg-orange-500',
      gradient: 'from-orange-400 to-orange-600'
    },
    {
      title: 'Academic Year',
      value: `S.Y. ${academicYear}`,
      icon: GraduationCap,
      color: 'bg-green-500',
      gradient: 'from-green-400 to-green-600'
    },
    { 
      title: 'Available Documents', 
      value: archiveStats.total.toString(), 
      icon: Archive, 
      color: 'bg-amber-500',
      gradient: 'from-amber-400 to-amber-600',
      highlighted: true
    }
  ];

  // Time-of-day greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 6) return { text: 'Good Evening', icon: Moon, period: 'night' };
    if (hour < 12) return { text: 'Good Morning', icon: Sunrise, period: 'morning' };
    if (hour < 17) return { text: 'Good Afternoon', icon: Sun, period: 'afternoon' };
    if (hour < 20) return { text: 'Good Evening', icon: Sunset, period: 'evening' };
    return { text: 'Good Evening', icon: Moon, period: 'night' };
  };
  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;

  // Today's schedule computations
  const todaysSchedules = useMemo(() => {
    const todayDow = new Date().getDay();
    return filteredSchedules
      .filter(s => s.dayOfWeek === todayDow)
      .sort((a, b) => {
        const [ah, am = 0] = a.startTime.split(':').map(Number);
        const [bh, bm = 0] = b.startTime.split(':').map(Number);
        return (ah * 60 + am) - (bh * 60 + bm);
      });
  }, [filteredSchedules]);

  const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes();

  const currentClass = useMemo(() => {
    return todaysSchedules.find(s => {
      const [sh, sm = 0] = s.startTime.split(':').map(Number);
      const [eh, em = 0] = s.endTime.split(':').map(Number);
      return nowMinutes >= (sh * 60 + sm) && nowMinutes < (eh * 60 + em);
    }) || null;
  }, [todaysSchedules, nowMinutes]);

  const nextClass = useMemo(() => {
    return todaysSchedules.find(s => {
      const [sh, sm = 0] = s.startTime.split(':').map(Number);
      return (sh * 60 + sm) > nowMinutes;
    }) || null;
  }, [todaysSchedules, nowMinutes]);

  const completedClasses = useMemo(() => {
    return todaysSchedules.filter(s => {
      const [eh, em = 0] = s.endTime.split(':').map(Number);
      return (eh * 60 + em) <= nowMinutes;
    }).length;
  }, [todaysSchedules, nowMinutes]);

  const dayProgress = todaysSchedules.length > 0
    ? Math.round((completedClasses / todaysSchedules.length) * 100)
    : 0;

  // Document archive categories for breakdown
  const archiveCategories = [
    { key: 'faculty-workload', label: 'Faculty Workload', icon: FileText, color: 'bg-blue-500' },
    { key: 'iso-forms', label: 'ISO Forms', icon: FolderOpen, color: 'bg-purple-500' },
    { key: 'unit-manual', label: 'Unit Manual', icon: BookOpen, color: 'bg-emerald-500' },
    { key: 'other', label: 'Other', icon: Archive, color: 'bg-slate-500' },
  ];

  // Function to fetch archive statistics from API
  const fetchArchiveStats = async () => {
    try {
      const token = await supabase.auth.getSession().then(({ data: { session } }) => session?.access_token || '');
      const response = await api.getArchiveStats(token);
      return response.stats;
    } catch (error) {
      console.error('Error fetching archive stats:', error);
      return {
        total: 0,
        'faculty-workload': 0,
        'iso-forms': 0,
        'unit-manual': 0,
        other: 0
      };
    }
  };

  useEffect(() => {
    // Initial fetch of archive stats
    const loadArchiveStats = async () => {
      const archiveStatsData = await fetchArchiveStats();
      setArchiveStats(archiveStatsData);
    };

    loadArchiveStats();

    // Set up custom event listener for document archive changes
    const handleArchiveChange = async () => {
      console.log('Document archive change detected');
      const archiveStatsData = await fetchArchiveStats();
      setArchiveStats(archiveStatsData);
    };

    // Listen for custom events dispatched when documents change
    window.addEventListener('archiveChanged', handleArchiveChange);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('archiveChanged', handleArchiveChange);
    };
  }, []);

  useEffect(() => {
    if (readArchiveTargetFromUrl()) {
      setActiveTab('archive');
    }

    const handleOpenArchiveTarget = () => {
      setActiveTab('archive');
    };

    window.addEventListener(ARCHIVE_TARGET_EVENT, handleOpenArchiveTarget);
    return () => {
      window.removeEventListener(ARCHIVE_TARGET_EVENT, handleOpenArchiveTarget);
    };
  }, []);

  useEffect(() => {
    // Fetch faculty schedules
    const fetchSchedules = async () => {
      try {
        setLoading(true);
        const token = await supabase.auth.getSession().then(({ data: { session } }) => session?.access_token || '');
        const response = await api.getFacultySchedules(user.id, token);
        setFacultySchedules(response.schedules || []);
      } catch (error) {
        console.error('Error fetching faculty schedules:', error);
        setFacultySchedules([]);
      } finally {
        setLoading(false);
      }
    };

    if (user.id) {
      fetchSchedules();
    }
  }, [user.id]);

  const getDayName = (dayOfWeek: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek] || 'Unknown';
  };

  // Convert 24-hour time to 12-hour format
  const formatTo12Hour = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/30 p-4 md:p-6 lg:p-8" role="region" aria-label="Faculty Dashboard Loading">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Skeleton className="w-12 h-12 rounded-xl" />
            <div className="space-y-2">
              <Skeleton className="h-7 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
          </div>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-8 w-16" />
                  </div>
                  <Skeleton className="h-11 w-11 rounded-xl" />
                </CardHeader>
              </Card>
            ))}
          </div>
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-64" />
            </CardHeader>
            <CardContent className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-24 w-full rounded-xl" />
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/30" role="region" aria-label="Faculty Dashboard">
      <div className={`space-y-4 md:space-y-6 p-4 md:p-6 lg:p-8 ${isMobile ? 'pb-24' : ''}`}>
        {/* Header Section with Greeting */}
        <header className="flex flex-col space-y-4">
          <div className="flex flex-col space-y-3 md:flex-row md:items-start md:justify-between md:space-y-0">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 animate-in fade-in slide-in-from-left-2 duration-500">
                  <LayoutDashboard className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                    Faculty Dashboard
                  </h1>
                  <p className="text-xs md:text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
                    <GreetingIcon className="h-3.5 w-3.5 text-amber-500" />
                    {greeting.text}, <span className="font-semibold text-foreground">{user.name}</span>
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1 ml-0.5">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="px-3 py-1.5 text-xs md:text-sm flex-shrink-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                <GraduationCap className="h-3 w-3 mr-1.5" />
                Faculty
              </Badge>
              {user.department && (
                <Badge variant="outline" className="px-3 py-1.5 text-xs md:text-sm flex-shrink-0">
                  {user.department}
                </Badge>
              )}
            </div>
          </div>

        {/* Navigation Bar */}
        <nav aria-label="Faculty dashboard navigation" className="flex items-center justify-between">
          <h2 className="text-lg md:text-xl font-bold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-500" />
            {getCurrentPageTitle()}
          </h2>
          {/* Mobile-only menu sheet */}
          <div className="md:hidden">
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 shadow-sm hover:shadow-md transition-all">
                  <Menu className="h-4 w-4" />
                  Menu
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80">
                <SheetHeader>
                  <SheetTitle>Navigation Menu</SheetTitle>
                  <SheetDescription>
                    Select a section to view
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-1">
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.value;
                    return (
                      <Button
                        key={item.value}
                        variant={isActive ? "secondary" : "ghost"}
                        className={`w-full justify-start transition-all duration-200 ${isActive ? 'bg-emerald-50 text-emerald-700 shadow-sm' : 'hover:bg-slate-50'}`}
                        onClick={() => handleMenuItemClick(item.value)}
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        {item.label}
                        {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                      </Button>
                    );
                  })}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
        </header>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          {/* Desktop inline tab navigation */}
          <div className="hidden md:block">
            <TabsList className="grid grid-cols-3 w-full max-w-lg h-auto bg-white/80 border border-slate-200 shadow-sm">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <TabsTrigger
                    key={item.value}
                    value={item.value}
                    className="flex items-center gap-2 py-2.5 px-3 text-sm transition-all duration-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-50 data-[state=active]:to-teal-50 data-[state=active]:text-emerald-700 data-[state=active]:shadow-sm"
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          <TabsContent value="overview" className="space-y-6">

            {/* Today at a Glance */}
            <section aria-label="Today at a glance">
              <Card className="overflow-hidden border-emerald-200/50 shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white px-4 sm:px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                        <Activity className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm sm:text-base">Today at a Glance</h3>
                        <p className="text-emerald-100 text-xs mt-0.5">
                          {todaysSchedules.length === 0 ? 'No classes today' : `${todaysSchedules.length} class${todaysSchedules.length !== 1 ? 'es' : ''} scheduled`}
                        </p>
                      </div>
                    </div>
                    {todaysSchedules.length > 0 && (
                      <div className="text-right hidden sm:block">
                        <div className="text-2xl font-bold">{completedClasses}/{todaysSchedules.length}</div>
                        <div className="text-emerald-100 text-xs">completed</div>
                      </div>
                    )}
                  </div>
                  {todaysSchedules.length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-emerald-100 mb-1.5">
                        <span>Day progress</span>
                        <span className="font-medium text-white">{dayProgress}%</span>
                      </div>
                      <Progress value={dayProgress} className="h-2 bg-white/20 [&>div]:bg-white" />
                    </div>
                  )}
                </div>
                <CardContent className="p-4 sm:p-5">
                  {todaysSchedules.length === 0 ? (
                    <div className="flex items-center gap-3 py-2">
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">You&apos;re free today!</p>
                        <p className="text-xs text-muted-foreground">No classes scheduled for today. Enjoy your day!</p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* Current/Next Class Spotlight */}
                      {currentClass && (
                        <div className="relative rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-4 overflow-hidden">
                          <div className="absolute top-3 right-3">
                            <span className="relative flex h-3 w-3">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                            </span>
                          </div>
                          <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px] mb-2">
                            <PlayCircle className="h-3 w-3 mr-1" /> IN PROGRESS
                          </Badge>
                          <h4 className="font-bold text-sm text-emerald-800">{currentClass.subject?.code}</h4>
                          <p className="text-xs text-emerald-700 mt-0.5">{currentClass.subject?.name}</p>
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center text-xs text-emerald-700">
                              <Clock className="w-3 h-3 mr-1.5" />
                              {formatTo12Hour(currentClass.startTime)} - {formatTo12Hour(currentClass.endTime)}
                            </div>
                            <div className="flex items-center text-xs text-emerald-700">
                              <MapPin className="w-3 h-3 mr-1.5" />
                              {currentClass.room?.name || 'TBA'}
                            </div>
                          </div>
                        </div>
                      )}
                      {nextClass && (
                        <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
                          <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] mb-2">
                            <ArrowRight className="h-3 w-3 mr-1" /> UP NEXT
                          </Badge>
                          <h4 className="font-bold text-sm text-blue-800">{nextClass.subject?.code}</h4>
                          <p className="text-xs text-blue-700 mt-0.5">{nextClass.subject?.name}</p>
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center text-xs text-blue-700">
                              <Clock className="w-3 h-3 mr-1.5" />
                              {formatTo12Hour(nextClass.startTime)} - {formatTo12Hour(nextClass.endTime)}
                            </div>
                            <div className="flex items-center text-xs text-blue-700">
                              <MapPin className="w-3 h-3 mr-1.5" />
                              {nextClass.room?.name || 'TBA'}
                            </div>
                          </div>
                        </div>
                      )}
                      {!currentClass && !nextClass && (
                        <div className="sm:col-span-2 flex items-center gap-3 py-2">
                          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">All classes done!</p>
                            <p className="text-xs text-muted-foreground">You&apos;ve completed all your classes for today.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Today's full schedule timeline */}
                  {todaysSchedules.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-xs font-medium text-muted-foreground mb-2.5">Today&apos;s Timeline</p>
                      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                        {todaysSchedules.map((s, idx) => {
                          const [eh, em = 0] = s.endTime.split(':').map(Number);
                          const isDone = (eh * 60 + em) <= nowMinutes;
                          const [sh, sm = 0] = s.startTime.split(':').map(Number);
                          const isNow = nowMinutes >= (sh * 60 + sm) && nowMinutes < (eh * 60 + em);
                          return (
                            <Tooltip key={s.id}>
                              <TooltipTrigger asChild>
                                <div className={`flex-shrink-0 rounded-lg px-3 py-2 border text-xs cursor-default transition-all duration-200 hover:scale-105 ${
                                  isNow
                                    ? 'bg-emerald-50 border-emerald-300 text-emerald-800 ring-2 ring-emerald-400/40 shadow-sm'
                                    : isDone
                                      ? 'bg-slate-50 border-slate-200 text-slate-400 line-through'
                                      : 'bg-white border-slate-200 text-slate-700 hover:border-blue-200 hover:bg-blue-50/30'
                                }`}>
                                  <div className="font-semibold">{s.subject?.code}</div>
                                  <div className="text-[10px] mt-0.5 opacity-75">{formatTo12Hour(s.startTime)}</div>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="text-xs">
                                <p className="font-semibold">{s.subject?.name}</p>
                                <p>{formatTo12Hour(s.startTime)} - {formatTo12Hour(s.endTime)}</p>
                                <p>{s.room?.name || 'TBA'}</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </section>

            {/* Stats Grid */}
            <section aria-label="Faculty statistics">
            <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
              {stats.filter(stat => !stat.highlighted).map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Card key={index} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden relative cursor-pointer active:scale-[0.98]"
                    onClick={() => {
                      if (stat.title === 'Total Classes' || stat.title === 'Subjects Teaching') setActiveTab('schedule');
                    }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                    <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2 sm:pb-3 p-3 sm:p-4 relative z-10">
                      <div className="space-y-1 flex-1 min-w-0">
                        <CardTitle className="text-[11px] sm:text-sm font-medium text-muted-foreground truncate">
                          {stat.title}
                        </CardTitle>
                        <div className="text-xl sm:text-2xl md:text-3xl font-bold truncate">{stat.value}</div>
                      </div>
                      <div className={`rounded-lg sm:rounded-xl p-2 sm:p-3 text-white bg-gradient-to-br ${stat.gradient} group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg flex-shrink-0`}>
                        <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
            </section>

            {/* Content Grid */}
            <section aria-label="Classes and quick actions">
            <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
              {/* My Classes - spans 2 columns on desktop */}
              <Card className="lg:col-span-2 hover:shadow-lg transition-all duration-300">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
                        <BookOpen className="h-4 w-4 text-white" />
                      </div>
                      My Classes
                    </CardTitle>
                    {facultySubjects.length > 0 && (
                      <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                        {facultySubjects.length} subject{facultySubjects.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                  <CardDescription>Your assigned subjects and schedules</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2.5 max-h-[400px] overflow-y-auto pr-1 scrollbar-thin">
                    {facultySubjects.map((subject, idx) => {
                      const colors = [
                        { bg: 'hover:bg-blue-50/50', border: 'hover:border-blue-200', accent: 'bg-blue-500', text: 'group-hover:text-blue-700', badge: 'bg-blue-50 border-blue-200 text-blue-700' },
                        { bg: 'hover:bg-violet-50/50', border: 'hover:border-violet-200', accent: 'bg-violet-500', text: 'group-hover:text-violet-700', badge: 'bg-violet-50 border-violet-200 text-violet-700' },
                        { bg: 'hover:bg-amber-50/50', border: 'hover:border-amber-200', accent: 'bg-amber-500', text: 'group-hover:text-amber-700', badge: 'bg-amber-50 border-amber-200 text-amber-700' },
                        { bg: 'hover:bg-rose-50/50', border: 'hover:border-rose-200', accent: 'bg-rose-500', text: 'group-hover:text-rose-700', badge: 'bg-rose-50 border-rose-200 text-rose-700' },
                        { bg: 'hover:bg-cyan-50/50', border: 'hover:border-cyan-200', accent: 'bg-cyan-500', text: 'group-hover:text-cyan-700', badge: 'bg-cyan-50 border-cyan-200 text-cyan-700' },
                        { bg: 'hover:bg-emerald-50/50', border: 'hover:border-emerald-200', accent: 'bg-emerald-500', text: 'group-hover:text-emerald-700', badge: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
                      ];
                      const color = colors[idx % colors.length];
                      return (
                        <div key={subject.id} className={`relative border border-slate-200 rounded-xl p-3.5 sm:p-4 ${color.bg} ${color.border} transition-all duration-200 group cursor-default`}>
                          <div className={`absolute left-0 top-3 bottom-3 w-1 rounded-full ${color.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-200`} />
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 space-y-1 sm:space-y-0">
                            <h4 className={`font-semibold text-sm md:text-base ${color.text} transition-colors truncate`}>{subject.code} - {subject.name}</h4>
                            <Badge variant="outline" className={`text-xs w-fit ${color.badge}`}>
                              <Users className="h-3 w-3 mr-1" />
                              {/*{subject.enrolledStudents.length} students*/}
                            </Badge>
                          </div>
                          <div className="text-xs md:text-sm text-muted-foreground space-y-1.5 mt-2 grid grid-cols-1 sm:grid-cols-3 gap-1.5 sm:gap-3">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                              <span className="truncate">{getDayName(subject.schedule.dayOfWeek)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                              <span className="truncate">{formatTo12Hour(subject.schedule.startTime)} - {formatTo12Hour(subject.schedule.endTime)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                              <span className="truncate">{subject.room?.name}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {facultySubjects.length === 0 && (
                      <div className="text-center py-10">
                        <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                          <BookOpen className="h-7 w-7 text-muted-foreground/30" />
                        </div>
                        <p className="text-muted-foreground text-sm font-medium">No classes assigned yet</p>
                        <p className="text-muted-foreground text-xs mt-1">Classes will appear here once assigned by the admin.</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Right sidebar - Quick Actions & Document Archive */}
              <div className="space-y-4">
                <Card className="hover:shadow-lg transition-all duration-300">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
                        <Sparkles className="h-3.5 w-3.5 text-white" />
                      </div>
                      Quick Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Dialog open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                      <DialogContent className={isMobile ? "max-w-[95vw] max-h-[90vh] overflow-y-auto p-3 sm:p-6" : "max-w-[95vw] lg:max-w-6xl max-h-[90vh] overflow-y-auto"}>
                        <DialogHeader className="pb-2">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 flex-shrink-0">
                              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                            </div>
                            <div className="min-w-0">
                              <DialogTitle className="text-base sm:text-lg">Weekly Class Schedule</DialogTitle>
                              <DialogDescription className="text-xs sm:text-sm">
                                Your complete schedule at a glance — color-coded by subject
                              </DialogDescription>
                            </div>
                          </div>
                        </DialogHeader>
                        <div className="-mx-1 sm:mx-0">
                          <FacultyScheduleCalendar facultyId={user.id} />
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Button 
                      variant="outline" 
                      className="w-full justify-between text-sm group hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-all h-11"
                      onClick={() => setActiveTab('schedule')}
                    >
                      <span className="flex items-center">
                        <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center mr-2.5 group-hover:bg-blue-200 transition-colors">
                          <Clock className="h-3.5 w-3.5 text-blue-600" />
                        </div>
                        My Schedule
                      </span>
                      <ChevronRight className="h-4 w-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </Button>

                    <Button 
                      variant="outline" 
                      className="w-full justify-between text-sm group hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700 transition-all h-11"
                      onClick={() => setActiveTab('archive')}
                    >
                      <span className="flex items-center">
                        <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center mr-2.5 group-hover:bg-amber-200 transition-colors">
                          <Archive className="h-3.5 w-3.5 text-amber-600" />
                        </div>
                        Document Archive
                      </span>
                      <ChevronRight className="h-4 w-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </Button>
                  </CardContent>
                </Card>

              </div>
            </div>
            </section>
          </TabsContent>

          <TabsContent value="schedule">
            <Card className="shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-emerald-50/80 to-teal-50/80 border-b border-emerald-100/50 px-4 sm:px-6 py-3 sm:py-4">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 flex-shrink-0">
                        <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <CardTitle className="text-base sm:text-lg">Weekly Schedule</CardTitle>
                        <CardDescription className="mt-0.5 text-xs sm:text-sm">Your complete class schedule — color-coded by subject</CardDescription>
                      </div>
                    </div>
                    <Badge variant="outline" className="w-fit text-[11px] sm:text-xs bg-white/80 border-emerald-200 text-emerald-700 px-2.5 sm:px-3 py-0.5 sm:py-1">
                      <Clock className="h-3 w-3 mr-1 sm:mr-1.5" />
                      {currentFilterSemesterLabel} • S.Y. {filterAcademicYear}
                    </Badge>
                  </div>
                  {/* Academic Year & Semester Filters */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    <div className="flex items-center gap-2 min-w-0 bg-white/70 border border-emerald-100 rounded-lg px-2.5 py-1.5">
                      <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Academic Year:</label>
                      <Select value={filterAcademicYear} onValueChange={setFilterAcademicYear}>
                        <SelectTrigger className="h-8 text-xs w-full bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableAcademicYears.map(year => (
                            <SelectItem key={year} value={year} className="text-xs">
                              S.Y. {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2 min-w-0 bg-white/70 border border-emerald-100 rounded-lg px-2.5 py-1.5">
                      <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Semester:</label>
                      <Select value={filterSemester} onValueChange={setFilterSemester}>
                        <SelectTrigger className="h-8 text-xs w-full bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {semesterOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value} className="text-xs">
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-2.5 sm:p-4 md:p-6">
                <FacultyScheduleCalendar facultyId={user.id} academicYear={filterAcademicYear} semester={filterSemester} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="archive">
            <DigitalArchive userRole="faculty" currentUserId={user.id} currentUserName={user.name} />
          </TabsContent>
        </Tabs>

        <AIAssistant user={user} />
      </div>

      {/* Mobile Bottom Tab Bar */}
      {isMobile && (
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] safe-area-pb" aria-label="Mobile navigation">
          <div className="flex items-center justify-around px-2 py-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.value;
              return (
                <button
                  key={item.value}
                  onClick={() => setActiveTab(item.value)}
                  className={`flex flex-col items-center justify-center py-2 px-3 rounded-xl min-w-[64px] transition-all duration-200 ${
                    isActive
                      ? 'text-emerald-700 bg-emerald-50'
                      : 'text-slate-500 hover:text-slate-700 active:bg-slate-50'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className={`h-5 w-5 transition-transform duration-200 ${isActive ? 'scale-110' : ''}`} />
                  <span className={`text-[10px] mt-0.5 font-medium transition-all ${isActive ? 'text-emerald-700' : 'text-slate-400'}`}>
                    {item.label}
                  </span>
                  {isActive && (
                    <div className="w-1 h-1 rounded-full bg-emerald-500 mt-0.5" />
                  )}
                </button>
              );
            })}
          </div>
        </nav>
      )}
    </div>
    </TooltipProvider>
  );
}